/**
 * Schedule next-run computation. Pure JS — runs on both client (optimistic
 * Zero mutator) and server (authoritative Zero mutator + DBOS scheduler).
 * Mirrors the original `computeNextRunAt` from
 * `packages/api/modules/automations/automations.service.ts`.
 */

interface ScheduleShape {
  type: 'hourly' | 'daily' | 'weekly' | 'monthly' | string
  hour?: number | null
  minute?: number | null
  dayOfMonth?: number | null
  dayOfWeek?: number | null
  timezone?: string | null
}

function getDatePartsInTz(date: Date, timezone: string) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  const parts = Object.fromEntries(
    fmt.formatToParts(date).map((p) => [p.type, p.value]),
  )
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour === '24' ? '0' : parts.hour),
    minute: Number(parts.minute),
  }
}

function dateInTz(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: string,
): Date {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0))
  const guessLocal = getDatePartsInTz(utcGuess, timezone)
  const diffMs =
    (guessLocal.hour * 60 + guessLocal.minute - (hour * 60 + minute)) * 60_000
  const dayDiff = guessLocal.day !== day ? (guessLocal.day > day ? 1 : -1) : 0
  return new Date(utcGuess.getTime() - diffMs - dayDiff * 86_400_000)
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function computeNextRunAt(
  schedule: ScheduleShape,
  after: Date = new Date(),
): Date {
  const tz = schedule.timezone ?? 'UTC'
  const now = getDatePartsInTz(after, tz)

  switch (schedule.type) {
    case 'hourly': {
      const candidate = dateInTz(now.year, now.month, now.day, now.hour, 0, tz)
      if (candidate.getTime() <= after.getTime()) {
        return new Date(candidate.getTime() + 3_600_000)
      }
      return candidate
    }
    case 'daily': {
      const h = schedule.hour ?? 0
      const m = schedule.minute ?? 0
      let candidate = dateInTz(now.year, now.month, now.day, h, m, tz)
      if (candidate.getTime() <= after.getTime()) {
        candidate = new Date(candidate.getTime() + 86_400_000)
      }
      return candidate
    }
    case 'monthly': {
      const h = schedule.hour ?? 0
      const m = schedule.minute ?? 0
      const targetDay = schedule.dayOfMonth ?? 1

      const clampedDay = Math.min(targetDay, daysInMonth(now.year, now.month))
      let candidate = dateInTz(now.year, now.month, clampedDay, h, m, tz)

      if (candidate.getTime() <= after.getTime()) {
        let nextMonth = now.month + 1
        let nextYear = now.year
        if (nextMonth > 12) {
          nextMonth = 1
          nextYear += 1
        }
        const clampedNextDay = Math.min(
          targetDay,
          daysInMonth(nextYear, nextMonth),
        )
        candidate = dateInTz(nextYear, nextMonth, clampedNextDay, h, m, tz)
      }

      return candidate
    }
    case 'weekly': {
      const h = schedule.hour ?? 0
      const m = schedule.minute ?? 0
      const targetDow = schedule.dayOfWeek ?? 0

      // Weekday of the current calendar date (Sunday=0), matching Date.getDay().
      const currentDow = new Date(
        Date.UTC(now.year, now.month - 1, now.day),
      ).getUTCDay()
      const daysUntil = (targetDow - currentDow + 7) % 7

      // Normalize the target calendar date so month/year overflow is handled
      // before it is passed to dateInTz.
      const target = new Date(
        Date.UTC(now.year, now.month - 1, now.day + daysUntil),
      )
      let candidate = dateInTz(
        target.getUTCFullYear(),
        target.getUTCMonth() + 1,
        target.getUTCDate(),
        h,
        m,
        tz,
      )

      if (candidate.getTime() <= after.getTime()) {
        const next = new Date(
          Date.UTC(now.year, now.month - 1, now.day + daysUntil + 7),
        )
        candidate = dateInTz(
          next.getUTCFullYear(),
          next.getUTCMonth() + 1,
          next.getUTCDate(),
          h,
          m,
          tz,
        )
      }

      return candidate
    }
    default:
      throw new Error(`Unknown schedule type: ${schedule.type}`)
  }
}
